import React, { useCallback, useMemo, useState } from 'react'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import offerPageURL from 'lib/offer/offerPageURL'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import LineTagAltIcon from 'components/Luxkit/Icons/line/LineTagAltIcon'
import moment from 'moment/moment'
import { LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import { offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import CruiseModalPricesItem from 'components/Cruises/CruiseTiles/Common/CruiseModalPrices/CruiseModalPricesItem'
import { sortBy, unique } from 'lib/array/arrayUtils'

const Prices = styled.div`
  padding: 0 ${rem(24)};
`

const Card = styled(Group)`
  border: 1px solid ${props => props.theme.palette.highlight.tertiary.lightForeground};
  transition: border-color 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
`

const CardBody = styled.div`
  padding: ${rem(12)} ${rem(10)};
`

interface Props {
  offerTitle: string;
  offer?: App.CruiseOffer | App.Offer | App.OfferSummary;
  departures?: Record<string, Array<App.CruiseDepartureSummary>>;
  departureList: Array<string>;
}

function CruiseModalPrices({
  offerTitle,
  offer,
  departures,
  departureList,
}: Props) {
  const dispatch = useAppDispatch()
  const [isOpen, toggleModal] = useToggle(true)

  const departureYears = useMemo(() => {
    const uniqueDepartureYears = unique(departureList.map(departureDate => departureDate.substring(0, 4)))
    return sortBy(uniqueDepartureYears, year => Number(year), 'asc')
  }, [departureList])

  const [selectedDepartureYear, setYear] = useState(departureYears[0] || '')

  const hasValidDepartures = useMemo(() => {
    if (!departures) { return false }

    return offerHasValidDepartures(offer as App.CruiseOffer)
  }, [departures, offer])

  const availableMonths = useMemo(() => {
    if (!departures || !departureList) { return [] }

    const availableMonths = departureList.filter((i) => i.includes(selectedDepartureYear))
    return sortBy(availableMonths, (month) => new Date(month).getTime(), 'asc')
  }, [departureList, departures, selectedDepartureYear])

  const onClickModal = useCallback((departureDate: string) => {
    toggleModal()

    const url = offerPageURL(offer)
    const month = moment(departureDate).format('YYYY-MM')
    dispatch(pushWithRegion(url, `departureMonths=${month}`))
    setTimeout(() => {
      window.location.href += '#departures-anchor'
    }, 2000)
  }, [toggleModal, dispatch, offer])

  const lowestPrice = useMemo(() => {
    if (!departures || !departureList?.length) { return null }

    const cruiseDepartures = Object.values(departures).flat()
    const price = Math.min(...cruiseDepartures.map(({ lowestPriceTotal }) => lowestPriceTotal))
    const departure = cruiseDepartures.find(({ lowestPriceTotal }) => lowestPriceTotal === price) ?? null
    return {
      price,
      departureDate: departure?.departureDate ?? null,
      arrivalDate: departure?.arrivalDate ?? null,
    }
  }, [departureList, departures])

  const getDeparturesByMonth = useCallback((month: string) => {
    if (!departures) { return [] }
    return sortBy((departures[month] || []), (departure) => departure.departureDate, 'asc')
  }, [departures])

  return <ModalBase height="auto" isOpen={isOpen} onClose={toggleModal}>
    <ModalHeader
      onCloseButtonClick={toggleModal}
      title={`Dates & Pricing for ${offerTitle}`}/>
    <ModalBody>

      <Prices>
        <Group direction="vertical" gap={32} data-testid="cruise-modal-prices-offer">
          {!!lowestPrice && <Card direction="vertical">
            <MessageBanner
              density="compact"
              description={<b>Lowest fare</b>}
              icon={<LineTagAltIcon/>}
              kind="promotional-secondary"
              trailingContent=""
            />
            <CardBody>
              <CruiseModalPricesItem
                lowestOffer
                departureDate={lowestPrice?.departureDate}
                arrivalDate={lowestPrice?.arrivalDate}
                price={lowestPrice.price}
                onClick={onClickModal}
              />
            </CardBody>
          </Card>}

          {departureYears.length > 1 && <TabButtonsGroup fit="grow-at-mobile">
            {departureYears.map((year, index) => <TabButton
              data-testid={`cruise-modal-prices-tabs-${year}`}
              key={index}
              size="medium"
              isActive={String(year) === selectedDepartureYear}
              onClick={() => setYear(year)}>{year}</TabButton>,
            )}
          </TabButtonsGroup>}

          <Group direction="vertical" gap={20}>
            {availableMonths.map((month, i) => <Group direction="vertical" gap={20} key={i}>
              <BodyText variant="large" weight="bold">{moment(month).format(LONG_MONTH_LONG_YEAR)}</BodyText>
              {getDeparturesByMonth(month).map((departure, j) => <div key={j}>
                <CruiseModalPricesItem
                  departureDate={departure?.departureDate}
                  arrivalDate={departure?.arrivalDate}
                  price={departure?.lowestPriceTotal}
                  onClick={onClickModal}
                />
              </div>)}
            </Group>)}
          </Group>
          {!hasValidDepartures && <CruiseSoldoutDepartureCard />}
          {!hasValidDepartures && <CruiseSoldoutStillInterested useLinkButton />}
        </Group>
      </Prices>

    </ModalBody>
  </ModalBase>
}

export default CruiseModalPrices
